import { useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import * as yup from 'yup';
import { Form as FinalForm, Field } from 'react-final-form';
import Form from '@guestyci/foundation/Form';
import { FormProvider } from '@guestyci/foundation/enums';
import TextField from '@guestyci/foundation/TextField';
import createStyles from '@guestyci/foundation/createStyles';
import defaultTheme from '@guestyci/foundation/theme/defaultTheme';
import { Row } from '@guestyci/foundation/Layout';
import InputField from 'app/shareable/inputs/InputField';
import useAuthorizationModel from 'app/hooks/authorization/useAuthorizationModel';
import utils from 'utils';
import ErrorMessage from 'app/shareable/ErrorMessage';
import { ReactComponent as IconSuccessSource } from 'assets/icons/Success.svg';
import FormButton from 'app/shareable/FormButton';
import ReCAPTCHA from 'react-google-recaptcha';
import config from 'config';

const useStyles = createStyles((theme: typeof defaultTheme) => ({
  primaryTextField: {
    color: theme.palette.success.default,
    display: 'flex',
  },
}));

const emailValidation = yup.object().shape({
  email: yup
    .string()
    .email('Invalid email address')
    .required('This field is required'),
});

const validate = utils.validateFormValues(emailValidation);

export default function ForgotPasswordForm() {
  const {
    resetPassword,
    isLoading,
    responseErrorMessage,
    responseSuccessMessage,
  } = useAuthorizationModel();
  const classes = useStyles();
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const email = queryParams.get('email') || '';

  const onSubmit = useCallback(async (values) => {
    const token = await recaptchaRef.current?.executeAsync();

    await resetPassword({
      email: values.email,
      token: token!,
    });
  }, []);

  return (
    <FinalForm
      onSubmit={onSubmit}
      initialValues={{ email }}
      validate={validate}
      render={({ handleSubmit }) => (
        <Form
          onSubmit={handleSubmit}
          noValidate
          provider={FormProvider.Final}
          fieldInstance={Field}
        >
          <InputField
            disabled={isLoading}
            label="Email"
            name="email"
            type="email"
            placeholder="Enter your email address"
          />

          <div className="mt-8">
            <FormButton disabled={isLoading} isLoading={isLoading}>
              SEND RESET LINK
            </FormButton>
          </div>
          <div className="mt-8">
            {responseSuccessMessage && (
              <TextField className={classes.primaryTextField}>
                <IconSuccessSource className="mr-2" />
                {responseSuccessMessage}
              </TextField>
            )}
            <Row>
              <ErrorMessage icon message={responseErrorMessage} />
            </Row>
          </div>
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={config.reCAPTCHASiteKey}
            size="invisible"
            badge="bottomright"
          />
        </Form>
      )}
    />
  );
}
